import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import resourceAllocationService from '../../../services/resourceAllocationService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import resourceRoleMasterService from '../../../services/resourceRoleMasterService'
import autoIncrementService from '../../../services/autoIncrementService'
import { 
  Users,
  Calendar,
  DollarSign,
  FileText,
  AlertCircle,
  FlaskConical,
  HelpCircle
} from 'lucide-react'

const ResourceAllocationForm = ({ mode = 'create', allocationId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [allocationDataLoading, setAllocationDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingAllocationNumber, setIsLoadingAllocationNumber] = useState(false)
  const hasLoadedAllocationDataRef = useRef(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: [],
    resourceRoles: []
  })

  const [formData, setFormData] = useState({
    allocationNumber: '',
    project: '',
    employee: '',
    resourceRole: '',
    allocationPercentage: 100,
    startDate: '',
    endDate: '',
    actualStartDate: '',
    actualEndDate: '',
    status: 'Planned',
    isBillable: true,
    billingRate: 0,
    currency: 'INR',
    estimatedCost: 0,
    actualCost: 0,
    estimatedHours: 0,
    actualHours: 0,
    hoursPerDay: 8,
    approvalStatus: 'Pending',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next allocation number (only for new allocations)
  useEffect(() => {
    if (!isEditMode && !isViewMode && !isLoadingAllocationNumber) {
      fetchNextAllocationNumber()
    }
  }, [isEditMode, isViewMode])

  // Load allocation data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && allocationId && masterDataLoaded && !hasLoadedAllocationDataRef.current) {
      if (initialData) {
        populateFormData(initialData)
        hasLoadedAllocationDataRef.current = true
      } else if (!allocationDataLoading) {
        loadAllocationData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationId, isEditMode, isViewMode, masterDataLoaded])

  const loadAllocationData = async () => {
    if (hasLoadedAllocationDataRef.current) {
      return
    }
    
    try {
      setAllocationDataLoading(true)
      hasLoadedAllocationDataRef.current = true
      const response = await resourceAllocationService.getResourceAllocationById(allocationId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load resource allocation')
        navigate('/projects/resource-allocations/list')
      }
    } catch (error) {
      console.error('Error loading resource allocation:', error)
      toast.error('Failed to load resource allocation')
      navigate('/projects/resource-allocations/list')
    } finally {
      setAllocationDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    // Extract IDs from populated objects
    let projectValue = ''
    if (data.project) {
      if (typeof data.project === 'object' && data.project !== null) {
        projectValue = String(data.project._id || data.project.id || '')
      } else {
        projectValue = String(data.project)
      }
    }

    let employeeValue = ''
    if (data.employee) {
      if (typeof data.employee === 'object' && data.employee !== null) {
        employeeValue = String(data.employee._id || data.employee.id || '')
      } else {
        employeeValue = String(data.employee)
      }
    }

    let resourceRoleValue = ''
    if (data.resourceRole) {
      if (typeof data.resourceRole === 'object' && data.resourceRole !== null) {
        resourceRoleValue = String(data.resourceRole._id || data.resourceRole.id || '')
      } else {
        resourceRoleValue = String(data.resourceRole)
      }
    }

    // Format dates
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    }

    setFormData({
      allocationNumber: data.allocationNumber || '',
      project: projectValue,
      employee: employeeValue,
      resourceRole: resourceRoleValue,
      allocationPercentage: data.allocationPercentage || 100,
      startDate: formatDate(data.startDate),
      endDate: formatDate(data.endDate),
      actualStartDate: formatDate(data.actualStartDate),
      actualEndDate: formatDate(data.actualEndDate),
      status: data.status || 'Planned',
      isBillable: data.isBillable !== undefined ? data.isBillable : true,
      billingRate: data.billingRate || 0,
      currency: data.currency || 'INR',
      estimatedCost: data.estimatedCost || 0,
      actualCost: data.actualCost || 0,
      estimatedHours: data.estimatedHours || 0,
      actualHours: data.actualHours || 0,
      hoursPerDay: data.hoursPerDay || 8,
      approvalStatus: data.approvalStatus || 'Pending',
      notes: data.notes || ''
    })
  }

  const fetchNextAllocationNumber = async () => {
    try {
      setIsLoadingAllocationNumber(true)
      const response = await autoIncrementService.getCurrentValue('RESOURCE-ALLOCATION')
      if (response.success && response.data) {
        const sequence = response.data
        if (sequence.formattedValue) {
          setFormData(prev => ({
            ...prev,
            allocationNumber: sequence.formattedValue
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            allocationNumber: 'RA-000001'
          }))
        }
      } else {
        setFormData(prev => ({
          ...prev,
          allocationNumber: 'RA-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next allocation number:', error)
      setFormData(prev => ({
        ...prev,
        allocationNumber: 'RA-000001'
      }))
    } finally {
      setIsLoadingAllocationNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes,
        resourceRolesRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        resourceRoleMasterService.getAllResourceRoleMasters({ limit: 1000 })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
          if (res.projects) {
            return res.projects
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      const getResourceRolesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            if (Array.isArray(res.data)) {
              return res.data
            }
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes),
        resourceRoles: getResourceRolesData(resourceRolesRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Resource Roles', result: resourceRolesRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 3) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      }

      // Auto-calculate estimated cost when billing rate, estimated hours, or allocation percentage changes
      if (field === 'billingRate' || field === 'estimatedHours' || field === 'allocationPercentage') {
        const billingRate = field === 'billingRate' ? parseFloat(value) || 0 : prev.billingRate
        const estimatedHours = field === 'estimatedHours' ? parseFloat(value) || 0 : prev.estimatedHours
        const allocationPercentage = field === 'allocationPercentage' ? parseFloat(value) || 0 : prev.allocationPercentage
        updated.estimatedCost = (billingRate * estimatedHours * allocationPercentage) / 100
      }

      // Auto-calculate actual cost when billing rate, actual hours, or allocation percentage changes
      if (field === 'billingRate' || field === 'actualHours' || field === 'allocationPercentage') {
        const billingRate = field === 'billingRate' ? parseFloat(value) || 0 : prev.billingRate
        const actualHours = field === 'actualHours' ? parseFloat(value) || 0 : prev.actualHours
        const allocationPercentage = field === 'allocationPercentage' ? parseFloat(value) || 0 : prev.allocationPercentage
        updated.actualCost = (billingRate * actualHours * allocationPercentage) / 100
      }

      return updated
    })
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.employee) {
      newErrors.employee = 'Employee is required'
    }
    if (!formData.resourceRole) {
      newErrors.resourceRole = 'Resource role is required'
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required'
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required'
    }
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date'
    }
    if (formData.allocationPercentage < 0 || formData.allocationPercentage > 100) {
      newErrors.allocationPercentage = 'Allocation percentage must be between 0 and 100'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      setLoading(true)

      let response
      if (isEditMode) {
        const { allocationNumber, ...updateData } = formData
        response = await resourceAllocationService.updateResourceAllocation(allocationId, updateData)
      } else {
        const { allocationNumber, ...createData } = formData
        response = await resourceAllocationService.createResourceAllocation(createData)
      }

      if (response.success) {
        toast.success(`Resource allocation ${isEditMode ? 'updated' : 'created'} successfully`)
        navigate('/projects/resource-allocations/list')
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} resource allocation`)
      }
    } catch (error) {
      console.error('Error saving resource allocation:', error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} resource allocation`)
    } finally {
      setLoading(false)
    }
  }

  const handleTestData = () => {
    const today = new Date()
    const endDate = new Date(today)
    endDate.setDate(today.getDate() + 90)

    setFormData(prev => ({
      ...prev,
      allocationNumber: prev.allocationNumber, // Preserve existing allocationNumber
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      employee: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      resourceRole: masterData.resourceRoles.length > 0 ? (masterData.resourceRoles[0]._id || masterData.resourceRoles[0].id) : '',
      allocationPercentage: 100,
      startDate: today.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: 'Active',
      isBillable: true,
      billingRate: 1500,
      currency: 'INR',
      estimatedHours: 160,
      actualHours: 0,
      hoursPerDay: 8,
      approvalStatus: 'Pending',
      notes: 'Test resource allocation data'
    }))
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Resource Allocations', path: '/projects/resource-allocations/list', onClick: () => navigate('/projects/resource-allocations/list') },
    { label: isEditMode ? 'Edit Allocation' : isViewMode ? 'View Allocation' : 'Create New', path: isEditMode ? `/projects/resource-allocations/edit/${allocationId}` : isViewMode ? `/projects/resource-allocations/view/${allocationId}` : '/projects/resource-allocations/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: Users },
    { id: 'time', label: 'Time & Cost', icon: DollarSign },
    { id: 'additional', label: 'Additional Info', icon: FileText }
  ]

  const helpContent = (
    <>
      <h3>What is Resource Allocation Entry?</h3>
      <p>Resource Allocation Entry lets you <strong>create and manage resource planning and team assignments</strong>. You assign a resource (employee) to a project with role, start/end dates, allocation percentage, and optional cost.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Allocation number, project, resource (employee), role, start date, end date, allocation percentage, status.</p>
      <p><strong>Time &amp; Cost</strong> – Hours, rate, cost, and billing-related fields.</p>
      <p><strong>Additional Info</strong> – Notes and optional details.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong> and <strong>resource</strong>, set role and dates. Use <strong>Create Allocation</strong> to save. Use the list to view all allocations and utilization.</p>
    </>
  )

  if (allocationDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading resource allocation data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Resource Allocation Entry</h1>
          <p>Create and manage resource allocation information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Resource Allocation Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    project: 'Project',
                    employee: 'Employee',
                    resourceRole: 'Resource Role',
                    startDate: 'Start Date',
                    endDate: 'End Date',
                    allocationPercentage: 'Allocation Percentage'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>
                
                {/* Row 1: Allocation Number, Project, Employee */}
                <div className="grid-row-3">
                  {/* Allocation Number */}
                  <div className="form-group">
                    <label htmlFor="allocationNumber" className="form-label">
                      Allocation Number
                    </label>
                    <input
                      id="allocationNumber"
                      type="text"
                      value={isLoadingAllocationNumber ? 'Loading...' : formData.allocationNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Project */}
                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.map((project) => {
                        const projectId = project._id || project.id
                        return (
                          <option key={projectId} value={String(projectId)}>
                            {project.projectNumber} - {project.projectName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>

                  {/* Employee */}
                  <div className="form-group">
                    <label htmlFor="employee" className="form-label required">
                      Employee
                    </label>
                    <select
                      id="employee"
                      value={formData.employee}
                      onChange={(e) => handleInputChange('employee', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Employee</option>
                      {masterData.employees.map((employee) => {
                        const employeeId = employee._id || employee.id
                        return (
                          <option key={employeeId} value={String(employeeId)}>
                            {employee.employeeName || employee.name}
                          </option>
                        )
                      })}
                    </select>
                    {errors.employee && (
                      <p className="form-error">{errors.employee}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Resource Role, Allocation Percentage, Status */}
                <div className="grid-row-3">
                  {/* Resource Role */}
                  <div className="form-group">
                    <label htmlFor="resourceRole" className="form-label required">
                      Resource Role
                    </label>
                    <select
                      id="resourceRole"
                      value={formData.resourceRole}
                      onChange={(e) => handleInputChange('resourceRole', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Role</option>
                      {masterData.resourceRoles.map((role) => {
                        const roleId = role._id || role.id
                        return (
                          <option key={roleId} value={String(roleId)}>
                            {role.resourceRoleName || role.roleName || role.name || 'N/A'}
                          </option>
                        )
                      })}
                    </select>
                    {errors.resourceRole && (
                      <p className="form-error">{errors.resourceRole}</p>
                    )}
                  </div>

                  {/* Allocation Percentage */}
                  <div className="form-group">
                    <label htmlFor="allocationPercentage" className="form-label required">
                      Allocation Percentage
                    </label>
                    <input
                      id="allocationPercentage"
                      type="number"
                      value={formData.allocationPercentage}
                      onChange={(e) => handleInputChange('allocationPercentage', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                      className="form-input"
                      min="0"
                      max="100"
                      step="1"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.allocationPercentage && (
                      <p className="form-error">{errors.allocationPercentage}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Planned">Planned</option>
                      <option value="Active">Active</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </div>
                </div>

                {/* Row 3: Start Date, End Date */}
                <div className="grid-row-3">
                  {/* Start Date */}
                  <div className="form-group">
                    <label htmlFor="startDate" className="form-label required">
                      Start Date
                    </label>
                    <input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.startDate && (
                      <p className="form-error">{errors.startDate}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="form-group">
                    <label htmlFor="endDate" className="form-label required">
                      End Date
                    </label>
                    <input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.endDate && (
                      <p className="form-error">{errors.endDate}</p>
                    )}
                  </div>

                  {/* Actual Start Date */}
                  <div className="form-group">
                    <label htmlFor="actualStartDate" className="form-label">
                      Actual Start Date
                    </label>
                    <input
                      id="actualStartDate"
                      type="date"
                      value={formData.actualStartDate}
                      onChange={(e) => handleInputChange('actualStartDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Row 4: Actual End Date */}
                <div className="grid-row-3">
                  {/* Actual End Date */}
                  <div className="form-group">
                    <label htmlFor="actualEndDate" className="form-label">
                      Actual End Date
                    </label>
                    <input
                      id="actualEndDate"
                      type="date"
                      value={formData.actualEndDate}
                      onChange={(e) => handleInputChange('actualEndDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'time' && (
              <div>
                <div className="section-header">
                  <h3>Time & Cost Information</h3>
                </div>
                
                {/* Row 1: Is Billable, Billing Rate, Currency */}
                <div className="grid-row-3">
                  {/* Is Billable */}
                  <div className="form-group">
                    <label htmlFor="isBillable" className="form-label">
                      <input
                        type="checkbox"
                        id="isBillable"
                        checked={formData.isBillable}
                        onChange={(e) => handleInputChange('isBillable', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Is Billable
                    </label>
                  </div>

                  {/* Billing Rate */}
                  {formData.isBillable && (
                    <>
                      <div className="form-group">
                        <label htmlFor="billingRate" className="form-label">
                          Billing Rate
                        </label>
                        <input
                          id="billingRate"
                          type="number"
                          value={formData.billingRate}
                          onChange={(e) => handleInputChange('billingRate', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min="0"
                          step="0.01"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="currency" className="form-label">
                          Currency
                        </label>
                        <select
                          id="currency"
                          value={formData.currency}
                          onChange={(e) => handleInputChange('currency', e.target.value)}
                          className="form-input"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        >
                          <option value="INR">INR</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {/* Row 2: Estimated Hours, Actual Hours, Hours Per Day */}
                <div className="grid-row-3">
                  {/* Estimated Hours */}
                  <div className="form-group">
                    <label htmlFor="estimatedHours" className="form-label">
                      Estimated Hours
                    </label>
                    <input
                      id="estimatedHours"
                      type="number"
                      value={formData.estimatedHours}
                      onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.5"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Actual Hours */}
                  <div className="form-group">
                    <label htmlFor="actualHours" className="form-label">
                      Actual Hours
                    </label>
                    <input
                      id="actualHours"
                      type="number"
                      value={formData.actualHours}
                      onChange={(e) => handleInputChange('actualHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.5"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Hours Per Day */}
                  <div className="form-group">
                    <label htmlFor="hoursPerDay" className="form-label">
                      Hours Per Day
                    </label>
                    <input
                      id="hoursPerDay"
                      type="number"
                      value={formData.hoursPerDay}
                      onChange={(e) => handleInputChange('hoursPerDay', Math.min(24, Math.max(0, parseFloat(e.target.value) || 8)))}
                      className="form-input"
                      min="0"
                      max="24"
                      step="0.5"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Row 3: Estimated Cost, Actual Cost */}
                {formData.isBillable && (
                  <div className="grid-row-3">
                    {/* Estimated Cost */}
                    <div className="form-group">
                      <label htmlFor="estimatedCost" className="form-label">
                        Estimated Cost
                      </label>
                      <input
                        id="estimatedCost"
                        type="number"
                        value={formData.estimatedCost.toFixed(2)}
                        className="form-input"
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                      <small style={{ color: '#6B7280' }}>Auto-calculated</small>
                    </div>

                    {/* Actual Cost */}
                    <div className="form-group">
                      <label htmlFor="actualCost" className="form-label">
                        Actual Cost
                      </label>
                      <input
                        id="actualCost"
                        type="number"
                        value={formData.actualCost.toFixed(2)}
                        className="form-input"
                        readOnly
                        disabled
                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                      <small style={{ color: '#6B7280' }}>Auto-calculated</small>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'additional' && (
              <div>
                <div className="section-header">
                  <h3>Additional Information</h3>
                </div>
                
                {/* Approval Status */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="approvalStatus" className="form-label">
                      Approval Status
                    </label>
                    <select
                      id="approvalStatus"
                      value={formData.approvalStatus}
                      onChange={(e) => handleInputChange('approvalStatus', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter additional notes"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/resource-allocations/list')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Allocation' : 'Create Allocation'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Resource Allocation Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ResourceAllocationForm

